import { Bell, CheckCircle2, Link2, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { apiErrorMessage } from '../api/client'
import { connectionsApi, guardianApi, profileApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { ErrorMessage } from '../components/ErrorMessage'
import type { ConnectionRequestResponse, GuardianLinkViewResponse } from '../types/api'

/**
 * There is no notifications table in the API, so this feed is composed from the things that
 * actually happened: guardian link requests, connection requests and decisions, and the account
 * itself. Nothing here is invented - every row is a real record with a real timestamp.
 */
interface FeedItem {
  id: string
  icon: ReactNode
  title: ReactNode
  meta: string
  at: number
  unread: boolean
  to: string
}

function relative(at: number): string {
  const mins = Math.round((Date.now() - at) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return new Date(at).toLocaleDateString()
}

export function NotificationsPage() {
  const { profile } = useAuth()
  const [items, setItems] = useState<FeedItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const build = useCallback(async () => {
    if (!profile) return
    const me = profile.id

    const [links, connections] = await Promise.all([
      guardianApi.listMyLinks().catch(() => [] as GuardianLinkViewResponse[]),
      connectionsApi.listOwn().catch(() => [] as ConnectionRequestResponse[]),
    ])

    const otherIds = Array.from(
      new Set(connections.map((c) => (c.senderParticipantId === me ? c.receiverParticipantId : c.senderParticipantId))),
    )
    const cards = await Promise.all(otherIds.map((id) => profileApi.getCard(id).catch(() => null)))
    const nameOf = new Map(otherIds.map((id, i) => [id, cards[i]?.fullName ?? 'A YellowDots participant']))

    const feed: FeedItem[] = []

    for (const link of links) {
      if (!link.awaitingMyApproval) continue
      feed.push({
        id: `link-${link.id}`,
        icon: <Link2 size={15} />,
        title: 'Someone asked to be linked as your parent or guardian',
        meta: 'Parent / Guardian · Approve only if the relationship is real.',
        at: link.createdAt ? new Date(link.createdAt).getTime() : Date.now(),
        unread: true,
        to: '/profile',
      })
    }

    for (const c of connections) {
      const otherId = c.senderParticipantId === me ? c.receiverParticipantId : c.senderParticipantId
      const name = nameOf.get(otherId) ?? 'A YellowDots participant'
      const iAmReceiver = c.receiverParticipantId === me

      if (c.status === 'PENDING' && iAmReceiver) {
        feed.push({
          id: `req-${c.id}`,
          icon: <Bell size={15} />,
          title: <><strong className="text-ink-900">{name}</strong> sent you a connection request</>,
          meta: `${c.aboutCategory ?? 'Connection'} · waiting for your answer`,
          at: new Date(c.sentAt).getTime(),
          unread: true,
          to: '/connections',
        })
      }

      if (c.status === 'ACCEPTED' && c.respondedAt) {
        feed.push({
          id: `acc-${c.id}`,
          icon: <CheckCircle2 size={15} />,
          title: iAmReceiver
            ? <>You accepted <strong className="text-ink-900">{name}</strong> — you are connected</>
            : <><strong className="text-ink-900">{name}</strong> accepted your request — you are connected</>,
          meta: `${c.aboutCategory ?? 'Connection'} · contact details are available under Connections`,
          at: new Date(c.respondedAt).getTime(),
          unread: false,
          to: '/connections',
        })
      }
    }

    feed.push({
      id: 'welcome',
      icon: <Sparkles size={15} />,
      title: 'Welcome to YellowDots — your profile is live',
      meta: 'Complete it to get better matches',
      at: new Date(profile.createdAt).getTime(),
      unread: false,
      to: '/profile',
    })

    feed.sort((a, b) => b.at - a.at)
    setItems(feed)
  }, [profile])

  useEffect(() => {
    build()
      .catch((err) => setError(apiErrorMessage(err, 'Could not load your notifications')))
      .finally(() => setLoading(false))
  }, [build])

  return (
    <div>
      <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
        <Bell size={18} className="text-sun-500" /> Notifications
      </h1>
      <p className="text-sm text-ink-500">Requests, decisions and updates about your YellowDots connections.</p>

      <div className="mt-4">
        <ErrorMessage message={error} />
      </div>

      {loading ? (
        <div className="card-pad mt-3 text-sm text-ink-500">Loading...</div>
      ) : (
        <div className="mt-3 space-y-3">
          {items.map((item) => (
            <Link
              key={item.id}
              to={item.to}
              className={`card-pad flex items-start gap-3 transition hover:border-sun-400/60 ${
                item.unread ? 'border-sun-400/50 bg-sun-400/5' : ''
              }`}
            >
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sun-400/20 text-amber-700">
                {item.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm text-ink-700">{item.title}</span>
                <span className="mt-0.5 block text-xs text-ink-400">
                  {relative(item.at)} · {item.meta}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2 text-xs font-semibold text-ink-500">
                Open →
                {item.unread && <span className="h-2 w-2 rounded-full bg-sun-500" />}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
